<aside class="fb-rail">
  <a
    class="fb-rail__logo"
    [routerLink]="homeLink()"
    [attr.aria-label]="'SHARED.SYSTEM.HOME' | translate"
    data-testid="link-rail-home">
    <span class="fb-rail__logo-mark" aria-hidden="true"></span>
  </a>

  <mat-divider class="fb-rail__divider" />

  <button
    type="button"
    class="fb-rail__search"
    [attr.aria-label]="'SHARED.SYSTEM.SEARCH_EVERYTHING' | translate"
    [matTooltip]="'SHARED.SYSTEM.SEARCH_EVERYTHING' | translate"
    matTooltipPosition="right"
    data-testid="btn-rail-search"
    (click)="openSearch()">
    <span class="fb-rail__search-button" aria-hidden="true">
      <mat-icon>search</mat-icon>
    </span>
    <span class="fb-rail__search-hint" aria-hidden="true">{{ searchShortcutHint }}</span>
  </button>

  <mat-divider class="fb-rail__divider" />

  <nav class="fb-rail__nav" [attr.aria-label]="'SHARED.SYSTEM.PRIMARY_NAVIGATION' | translate">
    <ul class="fb-rail__list">
      @for (group of groups(); track group.key) {
        @if (group.hasDividerBefore) {
          <li class="fb-rail__list-divider" aria-hidden="true">
            <mat-divider class="fb-rail__divider fb-rail__divider--group" />
          </li>
        }

        <!-- Origin here, not on the destination, so one pop-out serves both branches below. -->
        <li class="fb-rail__list-item" cdkOverlayOrigin #groupTrigger="cdkOverlayOrigin">
          @if (group.link && !opensPopoutOnTap(group)) {
            <a
              class="fb-rail__item"
              [class.fb-rail__item--active]="isGroupActive(group)"
              [class.fb-rail__item--open]="openPopoutKey() === group.key"
              [routerLink]="group.link"
              [attr.aria-current]="isGroupActive(group) ? 'page' : null"
              [attr.aria-haspopup]="hasPopout(group) ? 'true' : null"
              [attr.aria-expanded]="hasPopout(group) ? openPopoutKey() === group.key : null"
              [attr.aria-controls]="hasPopout(group) ? popoutId(group.key) : null"
              [attr.data-testid]="testId('link-rail', group.title)"
              (mouseenter)="openOnHover(group.key)"
              (mouseleave)="closeOnPointerLeave()"
              (focus)="closeOtherPopout(group.key)"
              (keydown.arrowdown)="openFromKeyboard(group.key, $event)"
              (keydown.arrowright)="openFromKeyboard(group.key, $event)"
              (click)="followGroupLink(group.key, $event)">
              <span class="fb-rail__item-icon" aria-hidden="true">
                @if (usesBrandMark(group)) {
                  <span class="fb-rail__item-mark"></span>
                } @else {
                  <mat-icon>{{ iconFor(group) }}</mat-icon>
                }
              </span>
              <span class="fb-rail__item-label">{{ group.title | translate }}</span>
            </a>
          } @else {
            <button
              type="button"
              class="fb-rail__item"
              [class.fb-rail__item--active]="isGroupActive(group)"
              [class.fb-rail__item--open]="openPopoutKey() === group.key"
              [attr.aria-expanded]="openPopoutKey() === group.key"
              [attr.aria-controls]="popoutId(group.key)"
              [attr.aria-current]="isGroupActive(group) ? 'true' : null"
              [attr.data-testid]="testId('btn-rail', group.title)"
              (mouseenter)="openOnHover(group.key)"
              (mouseleave)="closeOnPointerLeave()"
              (focus)="closeOtherPopout(group.key)"
              (keydown.arrowdown)="openFromKeyboard(group.key, $event)"
              (keydown.arrowright)="openFromKeyboard(group.key, $event)"
              (click)="activate(group.key, $event)">
              <span class="fb-rail__item-icon" aria-hidden="true">
                @if (usesBrandMark(group)) {
                  <span class="fb-rail__item-mark"></span>
                } @else {
                  <mat-icon>{{ iconFor(group) }}</mat-icon>
                }
              </span>
              <span class="fb-rail__item-label">{{ group.title | translate }}</span>
            </button>
          }

          @if (hasPopout(group)) {
            <ng-template
              cdkConnectedOverlay
              [cdkConnectedOverlayOrigin]="groupTrigger"
              [cdkConnectedOverlayOpen]="openPopoutKey() === group.key"
              [cdkConnectedOverlayPositions]="submenuPositions"
              [cdkConnectedOverlayViewportMargin]="12"
              [cdkConnectedOverlayPush]="true"
              (overlayOutsideClick)="
                closeOnOutsideClick($event, groupTrigger.elementRef.nativeElement)
              ">
              <!--
                A labelled `nav` rather than `role="menu"`: the panel holds a heading, separators
                and links, which an ARIA menu may not contain, and the menu role would promise
                arrow-key semantics this disclosure does not implement. Tab walks the links, and
                the focus trap keeps them reachable from an overlay outside the rail's DOM.
              -->
              <nav
                class="fb-popout fb-popout--rail"
                cdkTrapFocus
                [cdkTrapFocusAutoCapture]="popoutCapturesFocus()"
                [id]="popoutId(group.key)"
                animate.enter="fb-popout--enter"
                [attr.aria-label]="group.title | translate"
                [attr.data-testid]="testId('popout', group.title)"
                (mouseenter)="openOnHover(group.key)"
                (mouseleave)="closeOnPointerLeave()">
                <p class="fb-popout__title">{{ group.title | translate }}</p>

                @for (section of group.sections; track $index; let isLastSection = $last) {
                  @if (section.label) {
                    <p class="fb-popout__section-label">{{ section.label | translate }}</p>
                  }

                  @for (item of section.items; track item.link) {
                    <a
                      class="fb-popout__item"
                      [class.fb-popout__item--nested]="!!section.label"
                      [class.fb-popout__item--current]="isActiveLink(item.link)"
                      [routerLink]="item.link"
                      [attr.aria-current]="isActiveLink(item.link) ? 'page' : null"
                      [attr.data-testid]="testId('link-popout', item.title)"
                      (click)="closePopout(true)">
                      <span class="fb-popout__item-label">{{ item.title | translate }}</span>
                    </a>

                    @if (item.hasDividerAfter) {
                      <mat-divider class="fb-popout__divider" />
                    }
                  }

                  @if (!isLastSection) {
                    <mat-divider class="fb-popout__divider" />
                  }
                }
              </nav>
            </ng-template>
          }
        </li>
      }
    </ul>
  </nav>

  <div class="fb-rail__footer">
    <button
      type="button"
      class="fb-rail__avatar"
      [class.fb-rail__avatar--open]="openPopoutKey() === profileKey"
      [attr.aria-expanded]="openPopoutKey() === profileKey"
      [attr.aria-controls]="popoutId(profileKey)"
      [attr.aria-label]="'SHARED.SYSTEM.USER_MENU' | translate"
      [matTooltip]="userData()?.name || userEmail()"
      matTooltipPosition="right"
      cdkOverlayOrigin
      #profileTrigger="cdkOverlayOrigin"
      data-testid="btn-rail-profile"
      (click)="activate(profileKey, $event)">
      @if (userData()?.pictureUuid; as pictureUuid) {
        <app-profile-picture
          [pictureUuid]="pictureUuid"
          [size]="42"
          [alt]="userData()?.name || ''" />
      } @else {
        <app-default-avatar
          shape="circle"
          [name]="userData()?.name || userEmail()"
          [size]="42"
          fallbackIcon="account_circle" />
      }
    </button>

    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="profileTrigger"
      [cdkConnectedOverlayOpen]="openPopoutKey() === profileKey"
      [cdkConnectedOverlayPositions]="profilePositions"
      [cdkConnectedOverlayViewportMargin]="12"
      [cdkConnectedOverlayPush]="true"
      (overlayOutsideClick)="closeOnOutsideClick($event, profileTrigger.elementRef.nativeElement)">
      <div
        class="fb-popout fb-popout--rail fb-popout--profile"
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="popoutCapturesFocus()"
        [id]="popoutId(profileKey)"
        role="group"
        [attr.aria-label]="'SHARED.SYSTEM.USER_MENU' | translate"
        data-testid="popout-profile">
        <div class="fb-popout__profile-head">
          @if (userData()?.pictureUuid; as pictureUuid) {
            <app-profile-picture
              [pictureUuid]="pictureUuid"
              [size]="40"
              [alt]="userData()?.name || ''" />
          } @else {
            <app-default-avatar
              shape="circle"
              [name]="userData()?.name || userEmail()"
              [size]="40"
              fallbackIcon="account_circle" />
          }
          <span class="fb-popout__profile-identity">
            <span class="fb-popout__profile-name">{{ userData()?.name }}</span>
            <span class="fb-popout__profile-email">{{ userEmail() }}</span>
          </span>
        </div>

        <mat-divider class="fb-popout__divider" />

        <a
          class="fb-popout__item"
          [routerLink]="['/', routes.PROFILE]"
          data-testid="link-profile-user-profile"
          (click)="closePopout(true)">
          <span class="fb-popout__item-icon" aria-hidden="true">
            <mat-icon>person</mat-icon>
          </span>
          <span class="fb-popout__item-label">{{
            'SIDEBAR.FOOTER.USER_MENU.USER_PROFILE' | translate
          }}</span>
        </a>

        <button
          type="button"
          class="fb-popout__item"
          data-testid="btn-profile-help"
          (click)="openHelp($event)">
          <span class="fb-popout__item-icon" aria-hidden="true">
            <mat-icon>help_outline</mat-icon>
          </span>
          <span class="fb-popout__item-label">{{
            'SIDEBAR.FOOTER.USER_MENU.HELP' | translate
          }}</span>
        </button>

        <mat-divider class="fb-popout__divider" />

        <button
          type="button"
          class="fb-popout__item"
          data-testid="btn-profile-log-out"
          (click)="logOut()">
          <span class="fb-popout__item-icon" aria-hidden="true">
            <mat-icon>logout</mat-icon>
          </span>
          <span class="fb-popout__item-label">{{
            'SIDEBAR.FOOTER.USER_MENU.LOG_OUT' | translate
          }}</span>
        </button>
      </div>
    </ng-template>
  </div>
</aside>
